import React, { useEffect, useRef, useState } from "react";
import {
  extractValues,
  updateUrlParams,
} from "../../validation/ValidationInput";
import { getLocations } from "../../services/locations.services";
import { getSockHolding } from "../../services/stock.services";
import { useDebounceEffect } from "../../hook/useDebounceEffect";
import { useResponsive } from "../../hook/useResponsive";
import InventoryMobile, { StockRow } from "./InventoryMobile";
import {
  Density,
  ListColumn,
  ListFilter,
  ListFilterBar,
  ListTable,
  PAGE_SIZES,
  TEXT,
  cells,
} from "../../components/list";

interface StockResponse {
  totalStock: number;
  dataStock: StockRow[];
  total: {
    totalInventoryValuation: number;
    totalUniqueItems: number;
    totalItems: number;
  };
}

/** Column filters of /api/inventory/stock/list, keyed by their URL param. */
const URL_KEYS: Record<string, string> = {
  searchSupplier: "supplier",
  searchSku: "sku",
  searchUOM: "uom",
  searchCost: "cost",
  searchQty: "qty",
  searchLineCost: "lineCost",
  searchInUse: "inUse",
};
/** An absent `location` param means the user's own venue, so "all" is written out. */
const ALL_LOCATIONS = "all";

const toAmount = (value: any): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

const Stocks = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [country, setCountry] = useState<any[]>([]);
  const [total, setTotal] = useState<any>({});
  const companyId = localStorage.getItem("companyId");
  const locationId = localStorage.getItem("locationId") ?? "";
  const userId = localStorage.getItem("userId");
  const { isTouchLayout } = useResponsive();
  const [language, setLanguage] = useState<any>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const initalFilter = {
    page: 1,
    // Phones page 25 rows so a page is a few screens of cards, not 14k px.
    limit: isTouchLayout ? 25 : 50,
    searchSupplier: "",
    searchSku: "",
    searchProductName: "",
    searchUOM: "",
    searchCost: "",
    searchQty: "",
    searchLineCost: "",
    searchInUse: "",
    location: locationId,
  };
  // True while the last change was a deliberate action that already fetched.
  const [checkRecall, setCheckReCall] = useState<boolean>(true);
  const inforTable: any = {
    title:
      language && language?.["No current inventory found"]
        ? language?.["No current inventory found"]
        : "No current inventory found",
    desc:
      language && language?.["Your current inventory is empty"]
        ? language?.["Your current inventory is empty"]
        : "Your current inventory is empty",
  };
  const [rows, setRows] = useState<StockRow[]>([]);
  const [count, setCount] = useState<number>(0);
  const [filter, setFilter] = useState<any>(initalFilter);
  const latestFilter = useRef<any>(initalFilter);
  const requestSeq = useRef<number>(0);
  const [density, setDensity] = useState<Density>("default");
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});

  const handleKeyDown = (event: KeyboardEvent) => {
    if ((event.ctrlKey || event.metaKey) && event.key === "f") {
      event.preventDefault();
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  };
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);
  useEffect(() => {
    if (window.languageContext) {
      setLanguage(window.languageContext.language);
    }
  }, [window, window.languageContext]);

  const getListContry = async () => {
    try {
      const response: any = await getLocations(userId, companyId, "", "", "");
      setCountry(response.data.data);
    } catch (error) {
      console.error("Error loading inventory:", error);
    }
  };

  const getTerms = async (data: any) => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const filters = {
        searchSupplier: data.searchSupplier || "",
        searchSku: data.searchSku || "",
        searchProductName: data.searchProductName || "",
        searchUOM: data.searchUOM || "",
        searchCost: data.searchCost || "",
        searchQty: data.searchQty || "",
        searchLineCost: data.searchLineCost || "",
        searchInUse: data.searchInUse || "",
      };

      const response = await getSockHolding(
        data?.page,
        data?.limit,
        filters,
        data?.location,
        companyId,
      );
      if (seq !== requestSeq.current) return;
      const value: StockResponse = response.data;
      const totalRows = value.totalStock ?? 0;
      const lastPage = Math.max(1, Math.ceil(totalRows / (data?.limit || 50)));
      if (totalRows > 0 && data?.page > lastPage) {
        // A page past the end (old link, fewer rows now): show the last page instead.
        const clamped = { ...data, page: lastPage };
        latestFilter.current = clamped;
        setFilter(clamped);
        window.history.replaceState(window.history.state, "", location.pathname + urlFor(clamped));
        getTerms(clamped);
        return;
      }
      setTotal(value.total);
      setRows(value.dataStock ?? []);
      setCount(value.totalStock ?? 0);
    } catch (error) {
      console.error("Error loading inventory:", error);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  };

  useDebounceEffect(
    () => {
      if (!checkRecall) {
        getTerms(latestFilter.current);
      }
    },
    [filter],
    700,
  );

  const urlFor = (next: any) => {
    const extra: Record<string, string> = {
      location: next.location === locationId ? "" : next.location || ALL_LOCATIONS,
    };
    Object.keys(URL_KEYS).forEach((key) => {
      extra[URL_KEYS[key]] = next[key];
    });
    return updateUrlParams(
      { searchValue: next.searchProductName, page: next.page, limit: next.limit },
      extra,
    );
  };

  useEffect(() => {
    const params: any = extractValues(location.search.split("&"));
    const parsedLimit = +params.limit;
    const restored: any = {
      ...initalFilter,
      page: +params.page || 1,
      limit: PAGE_SIZES.includes(parsedLimit) ? parsedLimit : initalFilter.limit,
      searchProductName: params.search ?? "",
      location:
        params.location === ALL_LOCATIONS ? "" : params.location || locationId,
    };
    Object.keys(URL_KEYS).forEach((key) => {
      restored[key] = params[URL_KEYS[key]] ?? "";
    });
    latestFilter.current = restored;
    setFilter(restored);
    getTerms(restored);
    getListContry();
    const handleNewToken = () => {
      getTerms(latestFilter.current);
      getListContry();
    };
    document.addEventListener("newToken", handleNewToken);
    return () => {
      document.removeEventListener("newToken", handleNewToken);
    };
  }, []);

  /** Chips, location, paging: fetch now and replace the URL in place, like the list kit. */
  const applyFilter = (next: any) => {
    latestFilter.current = next;
    setCheckReCall(true);
    setFilter(next);
    window.history.replaceState(window.history.state, "", location.pathname + urlFor(next));
    getTerms(next);
  };

  /** Typing: debounced fetch, and the URL entry is replaced rather than pushed. */
  const handleChangeSearch = (field_search: string, value: string) => {
    const next = { ...latestFilter.current, [field_search]: value, page: 1 };
    latestFilter.current = next;
    setCheckReCall(false);
    setFilter(next);
    window.history.replaceState(window.history.state, "", location.pathname + urlFor(next));
  };

  const handleReceivePageNumber = (pageNumber: number) => {
    applyFilter({ ...latestFilter.current, page: pageNumber });
  };

  const handleSelectLocation = (id: string) => {
    applyFilter({
      ...latestFilter.current,
      location: id !== "location" ? id : "",
      page: 1,
    });
  };

  if (isTouchLayout) {
    return (
      <InventoryMobile
        language={language}
        total={total}
        rows={rows}
        count={count}
        page={filter.page}
        limit={filter.limit || 25}
        loading={loading}
        locations={country}
        filter={filter}
        onSearchChange={handleChangeSearch}
        onSelectLocation={handleSelectLocation}
        onPageChange={handleReceivePageNumber}
        searchInputRef={searchInputRef}
        emptyState={inforTable}
      />
    );
  }

  const yesNo = (value: boolean) =>
    value ? (language?.["Yes"] ?? "Yes") : (language?.["No"] ?? "No");

  const qtyCell = (row: StockRow) => (
    <div
      className="font-inter"
      style={{ fontSize: "14px", color: TEXT, fontVariantNumeric: "tabular-nums" }}
    >
      {Number(row.quantity ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
    </div>
  );

  const columns: ListColumn<StockRow>[] = [
    {
      key: "supplier",
      label: "Supplier",
      width: 180,
      hideable: false,
      value: (r) => r.Supplier || "—",
      mobile: "subtitle",
    },
    { key: "sku", label: "SKU", width: 120, value: (r) => r.sku, mobile: "subtitle" },
    {
      key: "product",
      label: "Product",
      width: 240,
      render: cells.primary((r) => r.product || "—"),
      value: (r) => r.product,
      mobile: "title",
    },
    { key: "uom", label: "UOM", width: 90, value: (r) => r.uom, mobile: "sheet" },
    {
      key: "cost",
      label: "Cost",
      width: 100,
      align: "right",
      render: cells.money((r) => toAmount(r.cost)),
      mobile: "sheet",
    },
    {
      key: "qty",
      label: "Qty",
      width: 90,
      align: "right",
      render: qtyCell,
      mobile: "sheet",
    },
    {
      key: "lineCost",
      label: "Line Cost",
      width: 110,
      align: "right",
      render: cells.money((r) => toAmount(r.linecost)),
      mobile: "trailing",
    },
    {
      key: "inUse",
      label: "In Use",
      width: 80,
      value: (r) => yesNo(!!r.inuse),
      mobile: "sheet",
    },
  ];

  // The API matches searchInUse === "true"; anything else means "not in use".
  const filters: ListFilter[] = [
    { key: "searchSupplier", label: "Supplier", kind: "text" },
    { key: "searchSku", label: "SKU", kind: "text" },
    { key: "searchUOM", label: "UOM", kind: "text" },
    { key: "searchCost", label: "Cost", kind: "text" },
    { key: "searchQty", label: "Qty", kind: "text" },
    { key: "searchLineCost", label: "Line Cost", kind: "text" },
    {
      key: "searchInUse",
      label: "In Use",
      kind: "select",
      options: [
        { value: "true", label: yesNo(true) },
        { value: "false", label: yesNo(false) },
      ],
    },
    {
      key: "location",
      label: "Location",
      kind: "select",
      options: (country ?? []).map((loc: any) => ({
        value: String(loc.id),
        label: loc.name,
      })),
      // The chip names the venue, never its id ("…" until locations load).
      valueLabel: (value: string) =>
        (country ?? []).find((loc: any) => String(loc.id) === value)?.name ?? "…",
    },
  ];
  const filterValues: Record<string, string> = filters.reduce(
    (acc: Record<string, string>, f) => {
      acc[f.key] = filter[f.key] ?? "";
      return acc;
    },
    {},
  );
  // The default venue is not a filter the user chose, so it keeps the empty-inventory state.
  const hasActiveQuery =
    String(filter.searchProductName ?? "").trim() !== "" ||
    filters.some((f) =>
      f.key === "location" ? filter.location !== locationId : !!filter[f.key],
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div className="">
        <div className="space-y-1">
          <p
            style={{ color: "#222525", fontSize: "24px" }}
            className="font-semibold font-inter"
          >
            {language && language?.["Current Inventory"]
              ? language?.["Current Inventory"]
              : "Current Inventory"}
          </p>
        </div>
        <div
          className={` items-center transition-all duration-1000 ease-in-out flex `}
          style={{
            gap: "20px",
            marginTop: "16px",
            flexWrap: "wrap",
          }}
        >
          <div
            className=""
            style={{
              width: "calc(33% - 10px)",
              border: "1px solid #EBF0F0",
              padding: "16px",
              borderRadius: "12px",
            }}
          >
            <p className="text-[14px] font-inter" style={{ color: "#4A5353" }}>
              {language && language?.["Inventory Valuation"]
                ? language?.["Inventory Valuation"]
                : "Inventory Valuation"}
            </p>
            <div className="flex items-center justify-between">
              <p className="text-[24px] text-main font-semibold font-inter">
                {(total?.totalInventoryValuation ?? 0).toFixed(2)} €
              </p>
            </div>
          </div>
          <div
            className=""
            style={{
              width: "calc(33% - 10px)",
              border: "1px solid #EBF0F0",
              padding: "16px",
              borderRadius: "12px",
            }}
          >
            <p className="text-[14px] font-inter" style={{ color: "#4A5353" }}>
              {language && language?.["Total Unique Items"]
                ? language?.["Total Unique Items"]
                : "Total Unique Items"}
            </p>
            <div className="flex items-center justify-between">
              <p className="text-[24px] text-main font-semibold font-inter">
                {total?.totalUniqueItems ?? 0}{" "}
              </p>
            </div>
          </div>
          <div
            className=""
            style={{
              width: "calc(33% - 10px)",
              border: "1px solid #EBF0F0",
              padding: "16px",
              borderRadius: "12px",
            }}
          >
            <p className="text-[14px] font-inter" style={{ color: "#4A5353" }}>
              {language && language?.["Total Items"]
                ? language?.["Total Items"]
                : "Total Items"}
            </p>
            <div className="flex items-center justify-between">
              <p className="text-[24px] text-main font-semibold font-inter">
                {total?.totalItems ?? 0}
              </p>
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          marginTop: "24px",
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        <ListFilterBar
          language={language}
          searchValue={filter.searchProductName}
          onSearchChange={(value: string) => handleChangeSearch("searchProductName", value)}
          searchPlaceholder="Search product name"
          searchInputRef={searchInputRef}
          filters={filters}
          filterValues={filterValues}
          onFilterChange={(key: string, value: string) =>
            applyFilter({ ...latestFilter.current, [key]: value, page: 1 })
          }
          columns={columns}
          visibleColumns={visibleColumns}
          onToggleColumn={(key: string) =>
            setVisibleColumns((prev) => ({ ...prev, [key]: prev[key] === false }))
          }
          density={density}
          onDensityChange={setDensity}
        />
        <div style={{ flex: 1, minHeight: 0 }}>
          <ListTable
            language={language}
            columns={columns}
            rows={rows}
            loading={loading}
            count={count}
            page={filter.page}
            limit={filter.limit}
            onPageChange={handleReceivePageNumber}
            onLimitChange={(limit: number) =>
              applyFilter({ ...latestFilter.current, limit, page: 1 })
            }
            emptyState={inforTable}
            hasActiveQuery={hasActiveQuery}
            visibleColumns={visibleColumns}
            density={density}
          />
        </div>
      </div>
    </div>
  );
};

export default Stocks;
